import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BoltOutlined, DonutLargeOutlined, LayersOutlined, TrendingDownOutlined, TrendingFlatOutlined, TrendingUpOutlined } from '@mui/icons-material'
import { Box } from '@mui/material'
import { Theme, useTheme } from '@mui/material/styles'

import { DescriptionHoverCard } from '../DescriptionHoverCard'
import { StatValueCard } from '../StatValueCard/StatValueCard'
import {
  CatalogueMetricKey,
  CatalogueNode,
  catalogueChangeLabelKey,
  catalogueChangeValue,
  formatActiveEvents,
  formatAdoptionPct,
  formatDensity,
  isCatalogueMetricOffered,
} from '../helpers/catalogueUtils'
import { renderStatTileValue } from '../helpers/statTiles'

const STAT_ICON_ADOPTION = <DonutLargeOutlined />
const STAT_ICON_DENSITY = <BoltOutlined />
const STAT_ICON_ACTIVE_EVENTS = <LayersOutlined />
const STAT_ICON_TREND_UP = <TrendingUpOutlined />
const STAT_ICON_TREND_DOWN = <TrendingDownOutlined />
const STAT_ICON_TREND_FLAT = <TrendingFlatOutlined />

function deltaColor(theme: Theme, delta: number | null): string {
  if (delta === null || delta === 0) return theme.palette.text.secondary
  return delta > 0 ? theme.palette.success.main : theme.palette.warning.main
}

function trendIcon(delta: number | null): React.ReactNode {
  if (delta === null || delta === 0) return STAT_ICON_TREND_FLAT
  return delta > 0 ? STAT_ICON_TREND_UP : STAT_ICON_TREND_DOWN
}

interface CatalogueRowHoverCardProps {
  node: CatalogueNode
  // Localized glossary copy for blueprint leaves and event types; event
  // categories have no editorial description of their own. Rich HTML for
  // blueprints, plain text for event types (see `isRichTextDescription`).
  description?: string
  // Change figure (percentage points) and its caption (the games behind it and
  // the window), computed by the row that owns the Change column so the column
  // and this card can't disagree on sign or denominator.
  changeValue?: string
  changeCaption?: string
  // The lens the row is shown under: it decides which change the tile above is and what
  // it is called (adoption change, or density change on the density lens).
  metric?: CatalogueMetricKey
}

/**
 * Hover card for a catalogue tree row: where the row sits in the taxonomy, what
 * it is, and the same figures its columns show, at a size worth reading.
 *
 * This is the quick-view panel's content, moved onto hover. The panel occupied a
 * third of the page for one selected blueprint at a time; hovering gets the same
 * answer for any row without spending layout on it, and without the navigation
 * ambiguity of a panel that opened on click while the row also expanded.
 *
 * Deliberately non-interactive: no links, and no info icons on the tile labels
 * even though `StatValueCard` supports them, since a tooltip inside a tooltip
 * can't be reached with the pointer.
 */
const CatalogueRowHoverCardComponent: React.FC<CatalogueRowHoverCardProps> = ({ node, description, changeValue, changeCaption, metric = 'adoption' }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const { adoptionPct, adoptionGames, cohortSize, density, activeEvents, blueprintCount, eventTypeCount } = node.metric
  const changeDelta = catalogueChangeValue(node, metric)

  // Ancestors only — the row's own name is the title right below. Event
  // categories are top level, so their trail is empty and the breadcrumb (and
  // its color dot) is dropped rather than left dangling.
  const trail = useMemo(
    () => [node.categoryName, node.kind === 'blueprint' ? node.eventTypeName : undefined].filter((name): name is string => Boolean(name)),
    [node.categoryName, node.eventTypeName, node.kind]
  )

  // What the row summarizes, for group rows. Blueprint leaves skip it: their
  // breadcrumb already says the same thing and the description says more.
  const counts = useMemo(() => {
    if (node.kind === 'eventCategory') {
      return `${t('market-analytics:catalogue_tree_event_type_count', { count: eventTypeCount })} · ${t('market-analytics:catalogue_tree_blueprint_count', {
        count: blueprintCount,
      })}`
    }
    if (node.kind === 'eventType') return t('market-analytics:catalogue_tree_blueprint_count', { count: blueprintCount })
    return undefined
  }, [node.kind, eventTypeCount, blueprintCount, t])

  const descriptionLabel =
    node.kind === 'eventType' ? t('market-analytics:detail_panel_what_this_event_type_is') : t('market-analytics:detail_panel_what_this_blueprint_is')

  const adoptionFraction =
    adoptionGames !== null && cohortSize !== null && cohortSize > 0
      ? t('market-analytics:catalogue_adoption_games_fraction', { adopting: adoptionGames, total: cohortSize })
      : undefined

  return (
    <DescriptionHoverCard
      name={node.name}
      trail={trail}
      colorHex={node.colorHex}
      discontinued={node.discontinued}
      subtitle={counts}
      description={description}
      descriptionLabel={descriptionLabel}
      isRichTextDescription={node.kind === 'blueprint'}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
        <StatValueCard
          label={t('market-analytics:detail_panel_stat_current_adoption')}
          accentColor={theme.palette.primary.main}
          backgroundIcon={STAT_ICON_ADOPTION}
          backgroundIconPlacement="contained"
          size="dense"
        >
          {renderStatTileValue(theme.palette.primary.main, formatAdoptionPct(adoptionPct), adoptionFraction)}
        </StatValueCard>

        {/* The change of the active lens, only when the catalogue actually carries one
            for this row — an all-"—" tile would just take up space. */}
        {changeDelta !== null && changeValue ? (
          <StatValueCard
            label={t(catalogueChangeLabelKey(metric))}
            accentColor={deltaColor(theme, changeDelta)}
            backgroundIcon={trendIcon(changeDelta)}
            backgroundIconPlacement="contained"
            size="dense"
          >
            {renderStatTileValue(deltaColor(theme, changeDelta), changeValue, changeCaption)}
          </StatValueCard>
        ) : null}

        {/* The catalogue only loads density / concurrent instances under those
            lenses (the endpoint returns per-day series chunked into ten-id
            requests), so on the adoption lens these tiles would be an em dash on
            every row. */}
        {density !== null ? (
          <StatValueCard
            label={t('market-analytics:detail_stat_density_label')}
            accentColor={theme.palette.secondary.main}
            backgroundIcon={STAT_ICON_DENSITY}
            backgroundIconPlacement="contained"
            size="dense"
          >
            {renderStatTileValue(theme.palette.secondary.main, formatDensity(density), t('market-analytics:detail_panel_stat_density_unit'))}
          </StatValueCard>
        ) : null}
        {activeEvents !== null && isCatalogueMetricOffered('activeEvents') ? (
          <StatValueCard
            label={t('market-analytics:active_live_events')}
            accentColor={theme.palette.secondary.main}
            backgroundIcon={STAT_ICON_ACTIVE_EVENTS}
            backgroundIconPlacement="contained"
            size="dense"
          >
            {renderStatTileValue(theme.palette.secondary.main, formatActiveEvents(activeEvents), t('market-analytics:active_live_events_window_mean_caption'))}
          </StatValueCard>
        ) : null}
      </Box>
    </DescriptionHoverCard>
  )
}

export const CatalogueRowHoverCard = React.memo(CatalogueRowHoverCardComponent)
CatalogueRowHoverCard.displayName = 'CatalogueRowHoverCard'
